import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { ChatConversation, ChatQueue, ChatSettings } from '../lib/types';

/**
 * Satış → Canlı sohbet (F6-16, ADR-097): çalışanın "sohbete açığım" durumu (panel açıkken 30 saniyede bir
 * bildirilir), bekleyen/etkin/son kapanan sohbetler, sohbet penceresi (üstlen, yanıtla, kapat, talebe dönüştür) ve
 * ayarlar. Gerçek zamanlı bağlantı yerine 5 saniyede bir yenilenir.
 */

const POLL_MS = 5000;
const HEARTBEAT_MS = 30_000;
const CATEGORIES = ['order', 'shipping', 'return', 'payment', 'product', 'account', 'other'] as const;
type Item = ChatQueue['waiting'][number];
const statusTone: Record<Item['status'], Tone> = { waiting: 'amber', active: 'pine', closed: 'neutral' };

export function LiveChatPage() {
  const t = useT();
  const { can } = usePermissions();
  const qc = useQueryClient();
  const manage = can('customers.chat.manage');
  const [selected, setSelected] = useState<string | null>(null);
  const queue = useQuery({
    queryKey: ['chat-queue'],
    queryFn: () => api.get<ChatQueue>('/v1/admin/chat/queue'),
    refetchInterval: POLL_MS,
  });
  const presence = useApiMutation(
    (available: boolean) => api.put<{ available: boolean }>('/v1/admin/chat/presence', { available }),
    { invalidate: [['chat-queue']] },
  );
  const q = queue.data;
  const available = !!q?.me.available;
  // Açıkken panel görüldü bilgisini yeniler; sayfadan çıkınca 90 saniye içinde çevrimdışı sayılır.
  useEffect(() => {
    if (!available || !manage) return;
    const id = window.setInterval(() => {
      void api
        .put('/v1/admin/chat/presence', { available: true })
        .then(() => qc.invalidateQueries({ queryKey: ['chat-queue'] }))
        .catch(() => undefined);
    }, HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [available, manage, qc]);
  if (!q) return <Spinner label={t('common.loading')} />;
  const list = (title: string, items: Item[], empty: string) => (
    <section aria-label={title} className="space-y-2">
      <h3 className="text-sm font-medium text-ink-2">
        {title} <span className="tabular text-ink-3">({items.length})</span>
      </h3>
      {!items.length ? (
        <p className="text-sm text-ink-3">{empty}</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                aria-current={selected === c.id ? 'true' : undefined}
                onClick={() => setSelected(c.id)}
                className={`w-full rounded-md border px-3 py-2 text-start text-sm ${
                  selected === c.id ? 'border-pine bg-surface-2' : 'border-line bg-surface hover:bg-surface-2'
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="font-medium">{c.name ?? t('admin.chat.anonymous')}</span>
                  <Badge tone={statusTone[c.status]}>{t(`admin.chat.statuses.${c.status}`)}</Badge>
                </span>
                <span className="block text-ink-3">
                  {c.assigneeName ? t('admin.chat.with', { name: c.assigneeName }) : (c.email ?? '—')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
  return (
    <PageFrame helpKey="page.live_chat">
      <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.chat.lead')}</p>
      {!q.enabled && (
        <p className="rounded-sm border border-amber/30 bg-amber-tint px-3 py-2 text-sm" role="status">
          {t('admin.chat.disabledNotice')}
        </p>
      )}
      <Section
        title={t('admin.chat.workspace')}
        action={
          manage && (
            <div className="flex items-center gap-2">
              <Switch
                id="chat-available"
                checked={available}
                onCheckedChange={(v) => presence.mutate(v)}
                disabled={presence.isPending}
              />
              <label htmlFor="chat-available" className="text-sm font-medium">
                {t('admin.chat.available')}
              </label>
              <HelpTip k="chat.available" />
            </div>
          )
        }
      >
        <p className="mb-4 text-sm text-ink-2" role="status">
          {t('admin.chat.onlineAgents', { count: q.onlineAgents })}
        </p>
        <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
          <div className="space-y-5">
            {list(t('admin.chat.waiting'), q.waiting, t('admin.chat.noWaiting'))}
            {list(t('admin.chat.active'), q.active, t('admin.chat.noActive'))}
            {list(t('admin.chat.closedRecent'), q.closed, t('admin.chat.noClosed'))}
          </div>
          <div>
            {selected ? (
              <ConversationPane key={selected} id={selected} />
            ) : (
              <EmptyState title={t('admin.chat.pickTitle')} description={t('admin.chat.pickLead')} />
            )}
          </div>
        </div>
      </Section>
      <SettingsSection />
    </PageFrame>
  );
}

function ConversationPane({ id }: { id: string }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const manage = can('customers.chat.manage');
  const [text, setText] = useState('');
  const [converting, setConverting] = useState(false);
  const logEnd = useRef<HTMLLIElement>(null);
  const conv = useQuery({
    queryKey: ['chat-conversation', id],
    queryFn: () => api.get<ChatConversation>(`/v1/admin/chat/conversations/${id}`),
    refetchInterval: (s) => (s.state.data?.status === 'closed' ? false : POLL_MS),
  });
  const invalidate = [['chat-conversation', id], ['chat-queue']];
  const claim = useApiMutation(() => api.post(`/v1/admin/chat/conversations/${id}/claim`), { invalidate });
  const send = useApiMutation(() => api.post(`/v1/admin/chat/conversations/${id}/messages`, { body: text }), {
    invalidate,
    onSuccess: () => setText(''),
    silentFieldErrors: true,
  });
  const close = useApiMutation(() => api.post(`/v1/admin/chat/conversations/${id}/close`), {
    invalidate,
    success: t('admin.chat.closedToast'),
  });
  const c = conv.data;
  useEffect(() => {
    logEnd.current?.scrollIntoView({ block: 'nearest' });
  }, [c?.messages.length]);
  if (!c) return <Spinner label={t('common.loading')} />;
  const open = c.status !== 'closed';
  return (
    <article className="space-y-4 rounded-md border border-line bg-surface p-4" aria-labelledby="chat-title">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id="chat-title" className="font-medium">
            {c.name ?? t('admin.chat.anonymous')}
          </h3>
          <p className="text-sm text-ink-3">
            {c.email ?? t('admin.chat.noEmail')}
            {c.customerId && (
              <>
                {' · '}
                <Link
                  to="/musteriler/$id"
                  params={{ id: c.customerId }}
                  className="text-pine underline underline-offset-2 hover:no-underline"
                >
                  {t('admin.chat.customerCard')}
                </Link>
              </>
            )}
          </p>
        </div>
        <Badge tone={statusTone[c.status]}>{t(`admin.chat.statuses.${c.status}`)}</Badge>
      </header>
      <div
        className="max-h-[26rem] overflow-y-auto rounded-sm"
        role="log"
        aria-live="polite"
        aria-label={t('admin.chat.messages')}
        tabIndex={0}
      >
        <ol className="space-y-2">
          {c.messages.map((m) =>
            m.author === 'system' ? (
              <li key={m.id} className="text-center text-xs text-ink-3">
                {t(`admin.chat.events.${m.body}`)} · {formatDate(m.createdAt, true)}
              </li>
            ) : (
              <li
                key={m.id}
                className={`rounded-md px-3 py-2 text-sm ${
                  m.author === 'staff' ? 'ms-8 bg-surface-2' : 'me-8 border border-line bg-surface'
                }`}
              >
                <p className="text-xs text-ink-3">
                  <span className="font-medium text-ink-2">
                    {m.author === 'staff' ? (m.authorName ?? '—') : (c.name ?? t('admin.chat.visitor'))}
                  </span>{' '}
                  {formatDate(m.createdAt, true)}
                </p>
                <p className="whitespace-pre-line break-words">{m.body}</p>
              </li>
            ),
          )}
          <li ref={logEnd} aria-hidden />
        </ol>
      </div>
      {c.ticketId && (
        <p className="text-sm">
          <Link
            to="/destek-talepleri/$id"
            params={{ id: c.ticketId }}
            className="text-pine underline underline-offset-2 hover:no-underline"
          >
            {t('admin.chat.openTicket')}
          </Link>
        </p>
      )}
      {manage && open && (
        <div className="space-y-3 border-t border-line pt-3">
          {c.status === 'waiting' && (
            <div className="flex items-center gap-1">
              <Button variant="primary" loading={claim.isPending} onClick={() => claim.mutate(undefined)}>
                {t('admin.chat.claim')}
              </Button>
              <HelpTip k="chat.claim" />
            </div>
          )}
          <Field
            label={t('admin.chat.reply')}
            help={<HelpTip k="chat.reply" />}
            description={t('admin.chat.replyHint')}
            error={fieldError(send.error, 'body')}
          >
            {(p) => (
              <Textarea
                {...p}
                className="min-h-20"
                maxLength={2000}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (text.trim()) send.mutate(undefined);
                  }
                }}
              />
            )}
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              disabled={!text.trim()}
              loading={send.isPending}
              onClick={() => send.mutate(undefined)}
            >
              {t('admin.chat.send')}
            </Button>
            <HelpTip k="chat.send" />
            <ConfirmButton
              variant="secondary"
              message={t('admin.chat.closeConfirm')}
              onConfirm={() => close.mutateAsync(undefined)}
            >
              {t('admin.chat.close')}
            </ConfirmButton>
            <HelpTip k="chat.close" />
          </div>
          {send.error && !send.error.errors.length && (
            <p role="alert" className="text-sm text-rose">
              {send.error.title}
            </p>
          )}
        </div>
      )}
      {can('customers.ticket.manage') && !c.ticketId && c.messages.some((m) => m.author === 'visitor') && (
        <div className="flex flex-wrap items-center gap-1 border-t border-line pt-3">
          <Button disabled={!c.hasEmail} onClick={() => setConverting(true)}>
            {t('admin.chat.convert')}
          </Button>
          <HelpTip k="chat.convert" />
          {!c.hasEmail && <span className="text-sm text-ink-3">{t('admin.chat.convertNeedsEmail')}</span>}
        </div>
      )}
      {converting && <ConvertDialog conv={c} onClose={() => setConverting(false)} />}
    </article>
  );
}

function ConvertDialog({ conv, onClose }: { conv: ChatConversation; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const first = conv.messages.find((m) => m.author === 'visitor')?.body ?? '';
  const [subject, setSubject] = useState(first.replace(/\s+/g, ' ').trim().slice(0, 80));
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('other');
  const convert = useApiMutation(
    () =>
      api.post<{ id: string; number: number }>(`/v1/admin/chat/conversations/${conv.id}/ticket`, {
        subject: subject.trim(),
        category,
        version: conv.version,
      }),
    {
      invalidate: [['chat-conversation', conv.id], ['chat-queue'], ['support-tickets']],
      success: (r) => t('admin.support.converted', { number: r.number }),
      onSuccess: (r) => {
        onClose();
        void navigate({ to: '/destek-talepleri/$id', params: { id: r.id } });
      },
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.chat.convertTitle')}
      description={t('admin.chat.convertLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={convert.isPending}
            disabled={subject.trim().length < 3}
            onClick={() => convert.mutate(undefined)}
          >
            {t('admin.chat.convert')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('admin.support.subject')}
          required
          help={<HelpTip k="support.convert_subject" />}
          error={fieldError(convert.error, 'subject')}
        >
          {(p) => (
            <Input {...p} value={subject} maxLength={160} onChange={(e) => setSubject(e.target.value)} />
          )}
        </Field>
        <Field label={t('admin.support.category')} help={<HelpTip k="support.category" />}>
          {(p) => (
            <Select
              {...p}
              value={category}
              onChange={(e) => setCategory(e.target.value as (typeof CATEGORIES)[number])}
            >
              {CATEGORIES.map((s) => (
                <option key={s} value={s}>
                  {t(`admin.support.categories.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {convert.error && !convert.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {convert.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function SettingsSection() {
  const { can } = usePermissions();
  const manage = can('customers.chat.manage');
  const q = useQuery({
    queryKey: ['chat-settings'],
    queryFn: () => api.get<ChatSettings>('/v1/admin/chat/settings'),
  });
  if (!q.data) return null;
  return <SettingsForm key={JSON.stringify(q.data)} saved={q.data} manage={manage} />;
}

function SettingsForm({ saved, manage }: { saved: ChatSettings; manage: boolean }) {
  const t = useT();
  const [enabled, setEnabled] = useState(saved.enabled);
  const [retention, setRetention] = useState(String(saved.retentionDays));
  const [idle, setIdle] = useState(String(saved.idleMinutes));
  const int = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : -1);
  const dirty =
    enabled !== saved.enabled ||
    retention !== String(saved.retentionDays) ||
    idle !== String(saved.idleMinutes);
  const save = useApiMutation(
    () =>
      api.put('/v1/admin/chat/settings', {
        enabled,
        retentionDays: int(retention),
        idleMinutes: int(idle),
      }),
    {
      invalidate: [['chat-settings'], ['chat-queue']],
      success: t('admin.chat.settingsSaved'),
      silentFieldErrors: true,
    },
  );
  return (
    <Section title={t('admin.chat.settings')} description={t('admin.chat.settingsLead')}>
      <fieldset disabled={!manage} className="grid max-w-3xl gap-4">
        <div className="flex items-center gap-2">
          <Switch id="chat-enabled" checked={enabled} onCheckedChange={setEnabled} disabled={!manage} />
          <label htmlFor="chat-enabled" className="text-sm font-medium">
            {t('admin.chat.enabled')}
          </label>
          <HelpTip k="chat.enabled" />
        </div>
        {!saved.privacyNotice && <p className="text-sm text-amber">{t('admin.chat.needsNotice')}</p>}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t('admin.chat.retentionDays')}
            help={<HelpTip k="chat.retention" />}
            error={fieldError(save.error, 'retentionDays')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={retention}
                onChange={(e) => setRetention(e.target.value)}
              />
            )}
          </Field>
          <Field
            label={t('admin.chat.idleMinutes')}
            help={<HelpTip k="chat.idle" />}
            error={fieldError(save.error, 'idleMinutes')}
          >
            {(p) => (
              <Input {...p} inputMode="numeric" value={idle} onChange={(e) => setIdle(e.target.value)} />
            )}
          </Field>
        </div>
        {save.error && !save.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {save.error.title}
          </p>
        )}
      </fieldset>
      {manage && (
        <div className="mt-4 flex items-center gap-1">
          <Button
            variant="primary"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.chat.saveSettings')}
          </Button>
          <HelpTip k="chat.save" />
        </div>
      )}
    </Section>
  );
}
